/**
 * AdvisorIntelligenceCard - Premium Advisor Card Component
 * ========================================================
 * SlabReality Advisor Intelligence Layer
 * 
 * Design Language:
 * - Dark cinematic luxury (#0A0A0A, #121212)
 * - Warm gold accents (#C8A36A, #B08B5C)
 * - Subtle muted status indicators (NOT neon green dots)
 * - Professional ecosystem feel (NOT social network)
 * 
 * @version 1.0 - AIL Foundation
 */

import React from 'react';
import { 
  Users, MapPin, Clock, MessageCircle, Star, 
  Globe, CheckCircle, Briefcase, Phone, Mail,
  Diamond, Layers, Sparkles, Sun, Building2,
  Columns, Home, Droplets, ChevronRight
} from 'lucide-react';

// =====================================================
// SPECIALTY ICON MAP
// =====================================================
const SPECIALTY_ICONS = {
  quartzite_specialist: Diamond,
  marble_expert: Layers,
  onyx_specialist: Sparkles,
  backlit_applications: Sun,
  bookmatch_expert: Layers,
  waterfall_specialist: Columns,
  hospitality_projects: Building2,
  luxury_residential: Home,
  spa_wellness: Droplets,
  default: Star
};

// =====================================================
// STATUS INDICATOR - Elegant, NOT chat-app style
// =====================================================
const StatusIndicator = ({ status, label }) => {
  // Muted, elegant colors - NOT neon
  const statusStyles = {
    online: { 
      dot: 'bg-emerald-400/80', 
      ring: 'ring-emerald-400/20',
      text: 'text-emerald-400/90',
      label: 'Online'
    },
    away: { 
      dot: 'bg-amber-400/80', 
      ring: 'ring-amber-400/20',
      text: 'text-amber-400/90',
      label: 'Assente'
    },
    busy: { 
      dot: 'bg-orange-400/80', 
      ring: 'ring-orange-400/20',
      text: 'text-orange-400/90',
      label: 'Occupato'
    },
    offline: { 
      dot: 'bg-neutral-500/60', 
      ring: 'ring-neutral-500/20',
      text: 'text-neutral-400',
      label: 'Non disponibile'
    }
  };

  const style = statusStyles[status] || statusStyles.offline;

  return (
    <div className="flex items-center gap-2">
      <div className={`relative flex items-center justify-center`}>
        {/* Subtle ring pulse for online */}
        {status === 'online' && (
          <span className={`absolute w-3 h-3 rounded-full ${style.ring} animate-ping opacity-50`} />
        )}
        {/* Status dot - smaller and more refined than chat apps */}
        <span className={`relative w-2 h-2 rounded-full ${style.dot}`} />
      </div>
      <span className={`text-xs font-medium ${style.text}`}>
        {label || style.label}
      </span>
    </div>
  );
};

// =====================================================
// AVAILABILITY BADGE
// =====================================================
const AvailabilityBadge = ({ status }) => {
  const badges = {
    available_now: { 
      text: 'Disponibile ora', 
      className: 'bg-[#C8A36A]/15 text-[#C8A36A] border-[#C8A36A]/30' 
    },
    available: { 
      text: 'Disponibile', 
      className: 'bg-[#C8A36A]/10 text-[#C8A36A]/80 border-[#C8A36A]/20' 
    },
    limited: { 
      text: 'Disponibilità limitata', 
      className: 'bg-amber-500/10 text-amber-400/80 border-amber-500/20' 
    },
    unavailable: { 
      text: 'Non disponibile', 
      className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' 
    },
    by_appointment: { 
      text: 'Su appuntamento', 
      className: 'bg-blue-500/10 text-blue-400/80 border-blue-500/20' 
    }
  };

  const badge = badges[status] || badges.available;

  return (
    <span className={`px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider rounded-full border ${badge.className}`}>
      {badge.text}
    </span>
  );
};

// =====================================================
// SPECIALTY TAG
// =====================================================
const SpecialtyTag = ({ specialty }) => {
  const Icon = SPECIALTY_ICONS[specialty.key] || SPECIALTY_ICONS.default;
  
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-white/[0.03] rounded-md border border-white/5 group-hover:border-[#C8A36A]/20 transition-colors">
      <Icon className="w-3 h-3 text-[#C8A36A]/70" />
      <span className="text-[10px] text-[#F5F1E8]/60">{specialty.label}</span>
    </div>
  );
};

// =====================================================
// ADVISOR CARD - Compact Version (for Material Detail sidebar)
// =====================================================
export const AdvisorCardCompact = ({ 
  advisor, 
  onClick,
  showDistance = false,
  matchReason = null
}) => {
  const {
    full_name,
    title,
    company,
    profile_image_url,
    online_status = 'offline',
    availability_status = 'available',
    city,
    operating_radius_km,
    specialties_display = [],
    response_time_display = '~24 ore',
    languages_display = 'IT',
    distance_km
  } = advisor;

  return (
    <div 
      onClick={onClick}
      className="group flex items-start gap-3 p-3 bg-white/[0.02] rounded-xl border border-white/5 hover:border-[#C8A36A]/30 hover:bg-white/[0.04] cursor-pointer transition-all duration-300"
    >
      {/* Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#C8A36A]/20 to-[#C8A36A]/5 flex items-center justify-center overflow-hidden border border-[#C8A36A]/10">
          {profile_image_url ? (
            <img src={profile_image_url} alt={full_name} className="w-full h-full object-cover" />
          ) : (
            <Users className="w-5 h-5 text-[#C8A36A]/70" />
          )}
        </div>
        {/* Status dot - subtle, positioned at bottom-right */}
        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0A0A0A] ${
          online_status === 'online' ? 'bg-emerald-400/80' :
          online_status === 'away' ? 'bg-amber-400/80' :
          online_status === 'busy' ? 'bg-orange-400/80' : 'bg-neutral-500/60'
        }`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <h4 className="text-sm font-medium text-[#F5F1E8] truncate group-hover:text-[#C8A36A] transition-colors">
            {full_name}
          </h4>
          {advisor.verified_status === 'verified' && (
            <CheckCircle className="w-3.5 h-3.5 text-[#C8A36A] flex-shrink-0" />
          )}
        </div>
        
        <p className="text-[11px] text-[#F5F1E8]/50 truncate">
          {title || company || 'Stone Consultant'}
        </p>
        
        {/* Match reason if provided */}
        {matchReason && (
          <p className="text-[10px] text-[#C8A36A]/70 mt-1 truncate">
            {matchReason}
          </p>
        )}
        
        {/* Specialties row */}
        {specialties_display.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {specialties_display.slice(0, 2).map((spec, i) => (
              <SpecialtyTag key={i} specialty={spec} />
            ))}
          </div>
        )}
      </div>

      {/* Right side info */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {showDistance && distance_km && (
          <span className="text-[10px] text-[#C8A36A]">{distance_km} km</span>
        )}
        {!showDistance && city && (
          <span className="text-[10px] text-[#F5F1E8]/40">{city}</span>
        )}
        <span className="text-[10px] text-[#F5F1E8]/30">{languages_display}</span>
      </div>
    </div>
  );
};

// =====================================================
// ADVISOR CARD - Full Version (for Advisor listing page)
// =====================================================
export const AdvisorCardFull = ({ 
  advisor, 
  onClick,
  onContact,
  index
}) => {
  const {
    full_name,
    title,
    company,
    bio_short,
    profile_image_url,
    online_status = 'offline',
    availability_status = 'available',
    city,
    state_region,
    country,
    operating_radius_km,
    specialties_display = [],
    material_specialties = [],
    project_categories = [],
    response_time_display = '~24 ore',
    response_rate_percent = 85,
    languages_display = 'IT',
    luxury_project_score = 50,
    verified_status = 'unverified'
  } = advisor;

  return (
    <div 
      className="group bg-[#121212] rounded-xl border border-white/5 hover:border-[#C8A36A]/30 transition-all duration-300 overflow-hidden"
    >
      {/* Header with gradient */}
      <div className="relative h-20 bg-gradient-to-br from-[#C8A36A]/10 via-transparent to-[#0A0A0A]">
        {/* Index badge */}
        {typeof index === 'number' && (
          <div className="absolute top-3 left-3 w-7 h-7 rounded-full bg-[#0A0A0A]/80 border border-[#C8A36A]/30 flex items-center justify-center">
            <span className="text-xs font-bold text-[#C8A36A]">{index + 1}</span>
          </div>
        )}
        
        {/* Status indicator - top right */}
        <div className="absolute top-3 right-3">
          <StatusIndicator status={online_status} />
        </div>
      </div>

      {/* Avatar - overlapping header */}
      <div className="relative -mt-10 px-4">
        <div className="relative inline-block">
          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#C8A36A]/20 to-[#1A1A1A] flex items-center justify-center overflow-hidden border-2 border-[#0A0A0A] shadow-lg">
            {profile_image_url ? (
              <img src={profile_image_url} alt={full_name} className="w-full h-full object-cover" />
            ) : (
              <Users className="w-8 h-8 text-[#C8A36A]/70" />
            )}
          </div>
          {/* Verified badge */}
          {verified_status === 'verified' && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0A0A0A] border border-[#C8A36A]/30 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 text-[#C8A36A]" />
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-4">
        {/* Name & Title */}
        <h3 className="text-lg font-medium text-[#F5F1E8] group-hover:text-[#C8A36A] transition-colors">
          {full_name}
        </h3>
        <p className="text-sm text-[#F5F1E8]/50 mb-3">
          {title || 'Stone Consultant'}{company ? ` • ${company}` : ''}
        </p>

        {/* Availability Badge */}
        <div className="mb-3">
          <AvailabilityBadge status={availability_status} />
        </div>

        {/* Bio */}
        {bio_short && (
          <p className="text-xs text-[#F5F1E8]/60 line-clamp-2 mb-3">
            {bio_short}
          </p>
        )}

        {/* Specialties */}
        {specialties_display.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {specialties_display.slice(0, 3).map((spec, i) => (
              <SpecialtyTag key={i} specialty={spec} />
            ))}
            {specialties_display.length > 3 && (
              <span className="text-[10px] text-[#F5F1E8]/40 px-2 py-1">
                +{specialties_display.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4 text-[10px] text-[#F5F1E8]/40 mb-4">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{response_time_display}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>{city}{state_region ? `, ${state_region}` : ''}</span>
          </div>
          <div className="flex items-center gap-1">
            <Globe className="w-3 h-3" />
            <span>{languages_display}</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onClick?.(advisor); }}
            className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#C8A36A] to-[#D4AF37] rounded-lg text-black text-sm font-medium hover:shadow-lg hover:shadow-[#C8A36A]/20 transition-all"
          >
            Vedi profilo
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onContact?.(advisor); }}
            className="py-2.5 px-4 bg-white/[0.03] border border-white/10 rounded-lg text-[#F5F1E8] text-sm hover:border-[#C8A36A]/30 hover:bg-white/[0.05] transition-all"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// ADVISOR SECTION - For Material Detail Page
// =====================================================
export const AdvisorSection = ({ 
  advisors = [], 
  isLoading = false,
  materialName,
  onAdvisorClick,
  onContactClick 
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#C8A36A]/10 flex items-center justify-center">
            <Users className="w-4 h-4 text-[#C8A36A]" />
          </div>
          <h2 className="text-lg font-medium text-[#F5F1E8]">Advisor Network</h2>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-6 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-[#C8A36A]/30 border-t-[#C8A36A] rounded-full animate-spin" />
          <span className="ml-3 text-sm text-[#F5F1E8]/60">Ricerca advisor...</span>
        </div>
      </div>
    );
  }

  if (!advisors || advisors.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#C8A36A]/10 flex items-center justify-center">
            <Users className="w-4 h-4 text-[#C8A36A]" />
          </div>
          <h2 className="text-lg font-medium text-[#F5F1E8]">Advisor Network</h2>
        </div>
        <div className="bg-white/[0.03] rounded-xl p-6 text-center">
          <Users className="w-10 h-10 text-[#F5F1E8]/20 mx-auto mb-3" />
          <p className="text-sm text-[#F5F1E8]/50">Nessun advisor disponibile per questo materiale</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C8A36A]/20 to-[#C8A36A]/5 flex items-center justify-center">
            <Users className="w-4 h-4 text-[#C8A36A]" />
          </div>
          <div>
            <h2 className="text-lg font-medium text-[#F5F1E8]">Advisor Network</h2>
            <p className="text-xs text-[#F5F1E8]/40">Consulenti specializzati per questo materiale</p>
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[#C8A36A]/60">
          {advisors.length} {advisors.length === 1 ? 'disponibile' : 'disponibili'}
        </span>
      </div>

      {/* Advisor Cards */}
      <div className="space-y-2">
        {advisors.map((advisor, i) => (
          <AdvisorCardCompact
            key={advisor.id || i}
            advisor={advisor}
            onClick={() => onAdvisorClick?.(advisor)}
            matchReason={advisor._match_reasons?.[0]}
          />
        ))}
      </div>

      {/* CTA */}
      <button 
        onClick={() => onContactClick?.()}
        className="w-full py-3 px-4 bg-white/[0.03] border border-[#C8A36A]/20 rounded-xl text-[#C8A36A] text-sm hover:bg-[#C8A36A]/10 transition-all flex items-center justify-center gap-2"
      >
        <MessageCircle className="w-4 h-4" />
        Richiedi consulenza
        <ChevronRight className="w-4 h-4 ml-auto" />
      </button>
    </div>
  );
};

export default AdvisorCardCompact;
